
import React, { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import I from "./Icon";
import apiService from "../services/apiService";
import brandLogo from "../component/Images/gore logo (1).jpg";

const ROLE_WISE_MENU_API =
  "MenuRoleMapping/GetRoleWiseMenuQuery";

const ALL_USER_ROLES_API =
  "UserRole/getAllUserRoles?includeInActive=true";

const USER_MANAGEMENT_ORDER = [
  "user-list",
  "user-role-list",
  "user-role-mapping",
  "menu-list",
  "menu-role-mapping",
];

const REPORTS_ORDER = [
  "report-teacher-wise-book-assign",
  "report-students",
  "report-pending-payments",
  "report-payments",
  "report-student-book-assign",
  "report-district-wise-school"
];

export default function Sidebar({ page, handleNav, setOverlay, isOpen = false }) {
  const [userManagementItems, setUserManagementItems] = useState([]);
  const [mainMenuItems, setMainMenuItems] = useState([]);
  const [userManagementMenu, setUserManagementMenu] = useState(null);

  const [reportsItems, setReportsItems] = useState([]);
  const [reportsMenu, setReportsMenu] = useState(null);

  const storedUser = JSON.parse(
    sessionStorage.getItem("user") || "{}"
  );

  const firstName =
    storedUser?.firstName ??
    storedUser?.FirstName ??
    "";

  const lastName =
    storedUser?.lastName ??
    storedUser?.LastName ??
    "";

  const fullName =
    sessionStorage.getItem("fullName") ||
    `${firstName} ${lastName}`.trim() ||
    "User";

  const email =
    sessionStorage.getItem("email") ||
    storedUser?.email ||
    storedUser?.Email ||
    "";

  const teacherId = Number(
    sessionStorage.getItem("teacherId") ??
    storedUser?.teacherId ??
    storedUser?.TeacherId ??
    0
  );
  const studentId = Number(
    sessionStorage.getItem("studentId") ??
    storedUser?.studentId ??
    storedUser?.StudentId ??
    0
  );

  const roleName =
    studentId > 0
      ? "Student"
      : teacherId > 0
        ? "Teacher"
        : (
          storedUser?.roleName ??
          storedUser?.RoleName ??
          sessionStorage.getItem("roleName") ??
          "Admin"
        );

  const normalizedRole = String(roleName)
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

  const isAdmin =
    normalizedRole === "admin" ||
    normalizedRole === "superadmin";

  const initials =
    `${firstName.charAt(0)}${lastName.charAt(0)}`
      .toUpperCase() || "U";

  const [dropdownOpen, setDropdownOpen] = useState({});

  const POLICY_MENU_ID = "admin-policy-menu";

  const isUserManagementPage =
    userManagementItems.some(
      (item) => item.id === page
    );

  const [userManagementOpen, setUserManagementOpen] =
    useState(isUserManagementPage);

  useEffect(() => {
    if (isUserManagementPage) {
      setUserManagementOpen(true);
    }
  }, [isUserManagementPage]);

  const isReportsPage =
    reportsItems.some(
      (item) => item.id === page
    );

  const [reportsOpen, setReportsOpen] =
    useState(isReportsPage);

  useEffect(() => {
    if (isReportsPage) {
      setReportsOpen(true);
    }
  }, [isReportsPage]);

  const loadSidebarMenus = async () => {
    try {
      const role =
        sessionStorage.getItem("role") ||
        localStorage.getItem("role") ||
        "";

      const roleIds =
        sessionStorage.getItem("roleIds") ||
        localStorage.getItem("roleIds") ||
        "";

      let parsedRoleIds = [];

      if (roleIds) {
        try {
          const parsed =
            JSON.parse(roleIds);

          if (Array.isArray(parsed)) {
            parsedRoleIds =
              parsed
                .map((item) =>
                  String(item).trim()
                )
                .filter(Boolean);
          } else if (
            parsed !== null &&
            parsed !== undefined
          ) {
            parsedRoleIds = [
              String(parsed).trim()
            ].filter(Boolean);
          }
        } catch {
          parsedRoleIds =
            String(roleIds)
              .replace(/[\[\]"']/g, "")
              .split(",")
              .map((item) =>
                item.trim()
              )
              .filter(Boolean);
        }
      }

      /*
        Resolve the real UserRole Id for Teacher / Student.

        Login roleIds is not always the same role id that
        MenuRoleMapping.GetRoleWiseMenuQuery expects. Admin happened
        to work, but Teacher / Student mappings can be stored against
        different UserRole ids.

        So for Teacher / Student we load UserRole list, match by
        RoleName, and use that backend role Id.
      */
      const normalizeRoleText = (value) =>
        String(value || "")
          .trim()
          .toLowerCase()
          .replace(/[\s_-]+/g, "");

      const expectedRoleName =
        studentId > 0
          ? "student"
          : teacherId > 0
            ? "teacher"
            : normalizeRoleText(
              storedUser?.roleName ??
              storedUser?.RoleName ??
              sessionStorage.getItem("roleName") ??
              role
            );

      let resolvedRoleId = "";

      if (
        expectedRoleName === "teacher" ||
        expectedRoleName === "student"
      ) {
        try {
          const rolesResponse =
            await apiService.get(
              ALL_USER_ROLES_API
            );

          const rolesResult =
            rolesResponse.data;

          const rolesOutput =
            rolesResult?.output ??
            rolesResult?.Output ??
            rolesResult?.data ??
            rolesResult?.Data ??
            rolesResult;

          const roleRows =
            Array.isArray(rolesOutput)
              ? rolesOutput
              : Array.isArray(
                rolesOutput?.result
              )
                ? rolesOutput.result
                : Array.isArray(
                  rolesOutput?.Result
                )
                  ? rolesOutput.Result
                  : [];

          const matchedRole =
            roleRows.find((item) => {
              const name =
                item?.roleName ??
                item?.RoleName ??
                item?.name ??
                item?.Name ??
                "";

              return (
                normalizeRoleText(name) ===
                expectedRoleName
              );
            });

          resolvedRoleId =
            matchedRole?.id ??
            matchedRole?.Id ??
            matchedRole?.userRoleId ??
            matchedRole?.UserRoleId ??
            "";
        } catch (roleResolveError) {
          console.error(
            "Sidebar UserRole resolve error:",
            roleResolveError
          );
        }
      }

      /*
        Priority:
        1. Resolved Teacher / Student UserRole id
        2. Login roleIds
        3. Login role as last fallback
      */
      const roleIdList =
        resolvedRoleId
          ? [String(resolvedRoleId)]
          : parsedRoleIds.length > 0
            ? Array.from(
              new Set(
                parsedRoleIds.filter(Boolean)
              )
            )
            : role
              ? [String(role).trim()]
              : [];

      console.log(
        "Sidebar Role:",
        expectedRoleName
      );

      console.log(
        "Sidebar Resolved Role Id:",
        resolvedRoleId
      );

      console.log(
        "Sidebar Role Ids Used:",
        roleIdList
      );

      if (roleIdList.length === 0) {
        setUserManagementItems([]);
        setUserManagementMenu(null);
        setReportsItems([]);
        setReportsMenu(null);
        setMainMenuItems([]);
        return;
      }

      const extractMenus = (result) => {
        const output =
          result?.output ??
          result?.Output ??
          result?.data ??
          result?.Data ??
          result;

        return Array.isArray(output)
          ? output
          : Array.isArray(output?.result)
            ? output.result
            : Array.isArray(output?.Result)
              ? output.Result
              : Array.isArray(output?.data)
                ? output.data
                : Array.isArray(output?.Data)
                  ? output.Data
                  : [];
      };

      const fetchRoleMenus = async (roleId) => {
        try {
          const response =
            await apiService.get(
              `${ROLE_WISE_MENU_API}/${encodeURIComponent(roleId)}`
            );

          const result =
            response.data;

          return extractMenus(result);
        } catch (error) {
          console.error(
            `GetRoleWiseMenuQuery failed for role ${roleId}:`,
            error?.response?.status,
            error?.response?.data
          );

          throw error;
        }
      };

      const roleMenuResults =
        await Promise.all(
          roleIdList.map(async (roleId) => {
            try {
              return await fetchRoleMenus(roleId);
            } catch (error) {
              console.error(
                `Sidebar Menu API Error for role ${roleId}:`,
                error
              );
              return [];
            }
          })
        );

      const getMenuBackendId = (menu) =>
        menu?.id ??
        menu?.Id ??
        menu?.menuId ??
        menu?.MenuId;

      const getSubItems = (menu) => {
        const items =
          menu?.subItems ??
          menu?.SubItems ??
          [];

        return Array.isArray(items)
          ? items
          : [];
      };

      const cloneMenu = (menu) => ({
        ...menu,
        subItems:
          getSubItems(menu).map(cloneMenu),
      });

      const mergeMenuLists = (target, source) => {
        source.forEach((menu) => {
          const menuId =
            getMenuBackendId(menu);

          const existing =
            target.find(
              (item) =>
                String(
                  getMenuBackendId(item)
                ) === String(menuId)
            );

          if (!existing) {
            target.push(
              cloneMenu(menu)
            );
            return;
          }

          existing.subItems =
            Array.isArray(existing.subItems)
              ? existing.subItems
              : [];

          mergeMenuLists(
            existing.subItems,
            getSubItems(menu)
          );
        });

        return target;
      };

      const menuResult =
        roleMenuResults.reduce(
          (allMenus, roleMenus) =>
            mergeMenuLists(
              allMenus,
              roleMenus
            ),
          []
        );

      const normalizeMenu = (menu) => {
        const titleValue =
          menu?.isTitle ??
          menu?.IsTitle ??
          menu?.title ??
          menu?.Title ??
          false;

        const activeValue =
          menu?.isActive ??
          menu?.IsActive ??
          menu?.active ??
          menu?.Active;

        const subItems =
          menu?.subItems ??
          menu?.SubItems ??
          [];

        return {
          backendId:
            menu?.id ??
            menu?.Id ??
            menu?.menuId ??
            menu?.MenuId,

          label:
            menu?.label ??
            menu?.Label ??
            "",

          icon:
            menu?.icon ??
            menu?.Icon ??
            "",

          link:
            menu?.link ??
            menu?.Link ??
            "",

          parentId:
            menu?.parentId ??
            menu?.ParentId ??
            0,

          title:
            titleValue === true ||
            Number(titleValue) === 1 ||
            String(titleValue)
              .toLowerCase() === "true",

          active:
            activeValue === undefined ||
              activeValue === null
              ? true
              : (
                activeValue === true ||
                Number(activeValue) === 1 ||
                String(activeValue)
                  .toLowerCase() === "true"
              ),

          subItems:
            Array.isArray(subItems)
              ? subItems
              : [],
        };
      };

      const roleWiseMenus =
        menuResult
          .map(normalizeMenu)
          .filter(
            (menu) =>
              menu.active &&
              menu.backendId &&
              menu.label
          )
          .sort(
            (a, b) =>
              Number(a.backendId) -
              Number(b.backendId)
          );

      /*
        Identify User Management explicitly.
        Other title menus (for example Reports) must remain
        separate dropdowns and must not be treated as User Management.
      */
      const userManagementParent =
        roleWiseMenus.find((rootMenu) => {
          const rootLabel = String(rootMenu?.label || "")
            .trim()
            .toLowerCase();

          const rootLink = String(rootMenu?.link || "")
            .trim()
            .toLowerCase();

          const childLinks = rootMenu.subItems
            .map(normalizeMenu)
            .filter((childMenu) => childMenu.active)
            .map((childMenu) => childMenu.link);

          return (
            rootMenu.title &&
            (
              rootLabel === "user management" ||
              rootLink === "user-management" ||
              childLinks.some((link) =>
                USER_MANAGEMENT_ORDER.includes(link)
              )
            )
          );
        });

      if (userManagementParent) {
        const children =
          userManagementParent.subItems
            .map(normalizeMenu)
            .filter(
              (menu) =>
                menu.active &&
                menu.backendId &&
                menu.label
            )
            .sort((a, b) => {
              const aIndex =
                USER_MANAGEMENT_ORDER.indexOf(
                  a.link
                );

              const bIndex =
                USER_MANAGEMENT_ORDER.indexOf(
                  b.link
                );

              if (
                aIndex === -1 &&
                bIndex === -1
              ) {
                return (
                  Number(a.backendId) -
                  Number(b.backendId)
                );
              }

              if (aIndex === -1) {
                return 1;
              }

              if (bIndex === -1) {
                return -1;
              }

              return aIndex - bIndex;
            })
            .map((menu) => ({
              id: menu.link,
              label: menu.label,
              icon: menu.icon,
              backendId:
                menu.backendId,
            }))
            .filter(
              (menu) => menu.id
            );

        setUserManagementMenu(
          userManagementParent
        );

        setUserManagementItems(
          children
        );
      } else {
        setUserManagementMenu(null);
        setUserManagementItems([]);
      }


    
      const reportsParent =
        roleWiseMenus.find((rootMenu) => {
          const rootLabel =
            String(rootMenu?.label || "")
              .trim()
              .toLowerCase();

          const rootLink =
            String(rootMenu?.link || "")
              .trim()
              .toLowerCase();

          return (
            rootLabel === "reports" ||
            rootLink === "reports"
          );
        });

      const reportsFromSubItems =
        reportsParent
          ? reportsParent.subItems
            .map(normalizeMenu)
            .filter(
              (menu) =>
                menu.active &&
                menu.backendId &&
                menu.label &&
                menu.link &&
                REPORTS_ORDER.includes(menu.link)
            )
          : [];

      const flatAssignedReports =
        roleWiseMenus
          .filter(
            (menu) =>
              menu.active &&
              menu.backendId &&
              menu.label &&
              menu.link &&
              REPORTS_ORDER.includes(menu.link)
          );

      const combinedReportsMap =
        new Map();

      [
        ...reportsFromSubItems,
        ...flatAssignedReports,
      ].forEach((menu) => {
        combinedReportsMap.set(
          String(menu.backendId ?? menu.link),
          menu
        );
      });

      const reportChildren =
        Array.from(
          combinedReportsMap.values()
        )
          .sort((a, b) => {
            const aIndex =
              REPORTS_ORDER.indexOf(a.link);

            const bIndex =
              REPORTS_ORDER.indexOf(b.link);

            if (
              aIndex === -1 &&
              bIndex === -1
            ) {
              return (
                Number(a.backendId) -
                Number(b.backendId)
              );
            }

            if (aIndex === -1) {
              return 1;
            }

            if (bIndex === -1) {
              return -1;
            }

            return aIndex - bIndex;
          })
          .map((menu) => ({
            id: menu.link,
            label: menu.label,
            icon: menu.icon,
            backendId: menu.backendId,
          }));

      if (reportChildren.length > 0) {
        setReportsMenu(
          reportsParent || {
            backendId: "reports-generated-parent",
            label: "Reports",
            link: "reports",
            icon: "file",
            title: true,
            active: true,
            subItems: [],
          }
        );

        setReportsItems(
          reportChildren
        );
      } else {
        setReportsMenu(null);
        setReportsItems([]);
      }



      const remainingRootMenus =
        roleWiseMenus
          .filter((menu) => {
            const isUserManagementParent =
              Number(menu.backendId) ===
              Number(
                userManagementParent?.backendId
              );

            const isReportsParent =
              reportsParent &&
              Number(menu.backendId) ===
              Number(
                reportsParent.backendId
              );

            const isFlatReportItem =
              REPORTS_ORDER.includes(
                String(menu.link || "")
              );

            return (
              !isUserManagementParent &&
              !isReportsParent &&
              !isFlatReportItem
            );
          })
          .sort((a, b) => {
            const aLabel =
              String(a?.label || "")
                .trim()
                .toLowerCase();

            const bLabel =
              String(b?.label || "")
                .trim()
                .toLowerCase();

            if (
              aLabel === "main menu" &&
              bLabel !== "main menu"
            ) {
              return -1;
            }

            if (
              bLabel === "main menu" &&
              aLabel !== "main menu"
            ) {
              return 1;
            }

            return (
              Number(a.backendId) -
              Number(b.backendId)
            );
          })
          .map((rootMenu) => {
            const children =
              rootMenu.subItems
                .map(normalizeMenu)
                .filter(
                  (menu) =>
                    menu.active &&
                    menu.backendId &&
                    menu.label
                )
                .sort(
                  (a, b) =>
                    Number(a.backendId) -
                    Number(b.backendId)
                )
                .map((menu) => ({
                  id: menu.link,
                  label: menu.label,
                  icon: menu.icon,
                  backendId:
                    menu.backendId,
                }))
                .filter(
                  (menu) => menu.id
                );

            return {
              id: rootMenu.link,
              icon:
                rootMenu.icon ||
                "dashboard",
              label:
                rootMenu.label,
              backendId:
                rootMenu.backendId,
              title:
                rootMenu.title,
              children,
            };
          });

      setMainMenuItems(
        remainingRootMenus
      );


      const openState = {};

      remainingRootMenus.forEach(
        (rootMenu) => {
          if (
            rootMenu.children.some(
              (child) =>
                child.id === page
            )
          ) {
            openState[
              rootMenu.backendId
            ] = true;
          }
        }
      );

      setDropdownOpen(
        (prev) => ({
          ...prev,
          ...openState,
        })
      );

    } catch (error) {
      console.error(
        "Sidebar Menu API Error:",
        error
      );

      setUserManagementItems([]);
      setUserManagementMenu(null);
      setReportsItems([]);
      setReportsMenu(null);
      setMainMenuItems([]);
    }
  };

  useEffect(() => {
    loadSidebarMenus();

    const handleMenusUpdated = () => {
      loadSidebarMenus();
    };

    window.addEventListener(
      "menusUpdated",
      handleMenusUpdated
    );

    return () => {
      window.removeEventListener(
        "menusUpdated",
        handleMenusUpdated
      );
    };
  }, []);


  useEffect(() => {
    setDropdownOpen(
      (prev) => {
        const next = {
          ...prev,
        };

        mainMenuItems.forEach(
          (menu) => {
            if (
              menu.children.some(
                (child) =>
                  child.id === page
              )
            ) {
              next[
                menu.backendId
              ] = true;
            }
          }
        );

        return next;
      }
    );
  }, [
    page,
    mainMenuItems,
  ]);


  const renderNavItem = (n) => (
    <div
      key={n.backendId ?? n.id}
      className={`nav-item${page === n.id ? " active" : ""}`}
      onClick={() => handleNav(n.id)}
    >
      <span className="nav-ic">
        <I n={n.icon} s={16} />
      </span>

      {n.label}

      {n.badge && (
        <span className={`nav-badge${n.blue ? " blue" : ""}`}>
          {n.badge}
        </span>
      )}
    </div>
  );


  const renderDropdownMenu = (menu) => {
    const isOpen =
      Boolean(
        dropdownOpen[
        menu.backendId
        ]
      );

    const isActive =
      menu.children.some(
        (child) =>
          child.id === page
      );

    return (
      <div
        key={menu.backendId}
        className="sb-section um-sidebar-section"
      >
        <button
          type="button"
          className={`um-sidebar-parent${isActive ? " active" : ""}`}
          onClick={() =>
            setDropdownOpen(
              (prev) => ({
                ...prev,
                [menu.backendId]:
                  !prev[
                  menu.backendId
                  ],
              })
            )
          }
        >
          <span className="nav-ic">
            <I
              n={
                menu.icon ||
                "settings"
              }
              s={17}
            />
          </span>

          <span>
            {menu.label}
          </span>

          <ChevronDown
            size={16}
            className={`um-sidebar-chevron${isOpen ? " open" : ""}`}
          />
        </button>

        {isOpen && (
          <div className="um-sidebar-submenu">
            {menu.children.map(
              (item) => (
                <button
                  type="button"
                  key={
                    item.backendId ??
                    item.id
                  }
                  className={`um-sidebar-subitem${page === item.id ? " active" : ""}`}
                  onClick={() =>
                    handleNav(item.id)
                  }
                >
                  <span className="um-sidebar-dash">
                    <I
                      n={item.icon || "dashboard"}
                      s={14}
                    />
                  </span>

                  <span>
                    {item.label}
                  </span>
                </button>
              )
            )}
          </div>
        )}
      </div>
    );
  };

  const renderReportsDropdown = () => {
    if (!reportsMenu || reportsItems.length === 0) {
      return null;
    }

    return (
      <div className="sb-section um-sidebar-section">
        <button
          type="button"
          className={`um-sidebar-parent${isReportsPage ? " active" : ""
            }`}
          onClick={() =>
            setReportsOpen(
              (prev) => !prev
            )
          }
        >
          <span className="nav-ic">
            <I
              n={reportsMenu.icon || "file"}
              s={17}
            />
          </span>

          <span>
            {reportsMenu.label}
          </span>

          <ChevronDown
            size={16}
            className={`um-sidebar-chevron${reportsOpen ? " open" : ""
              }`}
          />
        </button>

        {reportsOpen && (
          <div className="um-sidebar-submenu">
            {reportsItems.map(
              (item) => (
                <button
                  type="button"
                  key={
                    item.backendId ??
                    item.id
                  }
                  className={`um-sidebar-subitem${page === item.id
                    ? " active"
                    : ""
                    }`}
                  onClick={() =>
                    handleNav(item.id)
                  }
                >
                  <span className="um-sidebar-dash">
                    <I
                      n={item.icon || "file"}
                      s={14}
                    />
                  </span>

                  <span>
                    {item.label}
                  </span>
                </button>
              )
            )}
          </div>
        )}
      </div>
    );
  };

  const hasTransactionMenu =
    mainMenuItems.some((menu) => {
      const menuLabel =
        String(menu?.label || "")
          .trim()
          .toLowerCase();

      const menuId =
        String(menu?.id || "")
          .trim()
          .toLowerCase();

      return (
        menuLabel === "transaction" ||
        menuLabel === "transactions" ||
        menuId === "transaction" ||
        menuId === "transactions"
      );
    });



  return (
    <aside className={`sb${isOpen ? " mobile-open" : ""}`}>
      <div className="sb-brand">
        <div className="brand-logo">
          <div className="flex items-center gap-3">
            <img
              src={brandLogo}
              alt="Brand Logo"
              className="h-12 w-12 object-contain"
            />
          </div>
        </div>

        <div>
          <div className="brand-name">
            Gore Exam
          </div>

          <div className="brand-sub">
            Super Admin Panel
          </div>
        </div>
      </div>

      {/* User Management dropdown */}
      {userManagementMenu && (
        <div className="sb-section um-sidebar-section">
          <button
            type="button"
            className={`um-sidebar-parent${isUserManagementPage
              ? " active"
              : ""
              }`}
            onClick={() =>
              setUserManagementOpen(
                (prev) => !prev
              )
            }
          >
            <span className="nav-ic">
              <I
                n={
                  userManagementMenu.icon ||
                  "users"
                }
                s={17}
              />
            </span>

            <span>
              {userManagementMenu.label}
            </span>

            <ChevronDown
              size={16}
              className={`um-sidebar-chevron${userManagementOpen
                ? " open"
                : ""
                }`}
            />
          </button>

          {userManagementOpen && (
            <div className="um-sidebar-submenu">
              {userManagementItems.map(
                (item) => (
                  <button
                    type="button"
                    key={
                      item.backendId ??
                      item.id
                    }
                    className={`um-sidebar-subitem${page === item.id
                      ? " active"
                      : ""
                      }`}
                    onClick={() =>
                      handleNav(item.id)
                    }
                  >
                    <span className="um-sidebar-dash">
                      <I
                        n={item.icon || "users"}
                        s={14}
                      />
                    </span>

                    <span>
                      {item.label}
                    </span>
                  </button>
                )
              )}
            </div>
          )}
        </div>
      )}

      <div className="sb-section">
        {mainMenuItems.map((menu) => {
          const renderedMenu =
            menu.children.length > 0
              ? renderDropdownMenu(menu)
              : renderNavItem(menu);

          const menuLabel = String(menu?.label || "")
            .trim()
            .toLowerCase();

          const menuId = String(menu?.id || "")
            .trim()
            .toLowerCase();

          const isTransactionMenu =
            menuLabel === "transaction" ||
            menuLabel === "transactions" ||
            menuId === "transaction" ||
            menuId === "transactions";

          return (
            <React.Fragment key={menu.backendId ?? menu.id}>
              {renderedMenu}

              {isTransactionMenu &&
                renderReportsDropdown()}
            </React.Fragment>
          );
        })}

        {!hasTransactionMenu &&
          renderReportsDropdown()}
      </div>

      {/* POLICY - LAST MENU ITEM */}
      {isAdmin && (
        <div className="sb-section um-sidebar-section">
          <button
            type="button"
            className={`um-sidebar-parent${page === "terms-and-conditions" ||
              page === "privacy-policy" ||
              page === "shipping-and-delivery-policy" ||
              page === "cancellation-and-refund-policy"
              ? " active"
              : ""
              }`}
            onClick={() =>
              setDropdownOpen((prev) => ({
                ...prev,
                [POLICY_MENU_ID]: !prev[POLICY_MENU_ID],
              }))
            }
          >
            <span className="nav-ic">
              <I n="file" s={17} />
            </span>

            <span>Policy</span>

            <ChevronDown
              size={16}
              className={`um-sidebar-chevron${dropdownOpen[POLICY_MENU_ID] ? " open" : ""
                }`}
            />
          </button>

          {dropdownOpen[POLICY_MENU_ID] && (
            <div className="um-sidebar-submenu">
              <button
                type="button"
                className={`um-sidebar-subitem${page === "terms-and-conditions" ? " active" : ""
                  }`}
                onClick={() =>
                  handleNav("terms-and-conditions")
                }
              >
                <span className="um-sidebar-dash">
                  <I n="file" s={14} />
                </span>
                <span>Terms & Conditions</span>
              </button>

              <button
                type="button"
                className={`um-sidebar-subitem${page === "privacy-policy" ? " active" : ""
                  }`}
                onClick={() =>
                  handleNav("privacy-policy")
                }
              >
                <span className="um-sidebar-dash">
                  <I n="file" s={14} />
                </span>
                <span>Privacy Policy</span>
              </button>

              <button
                type="button"
                className={`um-sidebar-subitem${page === "shipping-and-delivery-policy"
                  ? " active"
                  : ""
                  }`}
                onClick={() =>
                  handleNav("shipping-and-delivery-policy")
                }
              >
                <span className="um-sidebar-dash">
                  <I n="file" s={14} />
                </span>
                <span>Shipping & Delivery Policy</span>
              </button>

              <button
                type="button"
                className={`um-sidebar-subitem${page === "cancellation-and-refund-policy"
                  ? " active"
                  : ""
                  }`}
                onClick={() =>
                  handleNav("cancellation-and-refund-policy")
                }
              >
                <span className="um-sidebar-dash">
                  <I n="file" s={14} />
                </span>
                <span>Cancellation & Refund Policy</span>
              </button>
            </div>
          )}
        </div>
      )}

      {/* <div className="sb-footer">
        <div
          className="user-row"
          onClick={() => setOverlay("profile")}
        >
          ...
        </div>
      </div> */}

      <div className="sb-footer">
        <div
          className="user-row"
          onClick={() => setOverlay("profile")}
        >
          <div className="av">
            {initials}
          </div>

          <div>
            <div className="user-name">
              {roleName}
            </div>

            <div className="user-role">

              {email ? ` ${email}` : ""}
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
}

